import React, { useEffect, useReducer, useState } from 'react';
import { toast } from 'react-toastify';
import { Trash2, Mail, Download } from 'lucide-react';
import PageHeader from '../../components/shared/PageHeader';
import { Button } from '../../components/ui/button';
import { Card, CardContent } from '../../components/ui/card';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '../../components/ui/table';
import ConfirmModal from '../../components/ConfirmModal';
import { getError } from '../../utils';
import newsletterService from '../../services/newsletter.service';

const reducer = (state, action) => {
  switch (action.type) {
    case 'FETCH_REQUEST':
      return { ...state, loading: true };
    case 'FETCH_SUCCESS':
      return { ...state, loading: false, subscribers: action.payload };
    case 'FETCH_FAIL':
      return { ...state, loading: false, error: action.payload };
    default:
      return state;
  }
};

const formatDate = iso => {
  if (!iso) return '—';
  try {
    return new Date(iso).toLocaleString();
  } catch {
    return iso;
  }
};

export default function NewsletterList() {
  const [{ loading, error, subscribers }, dispatch] = useReducer(reducer, {
    loading: true,
    error: '',
    subscribers: [],
  });

  const [toDelete, setToDelete] = useState(null);
  const [showDelete, setShowDelete] = useState(false);

  const fetchData = async () => {
    try {
      dispatch({ type: 'FETCH_REQUEST' });
      const data = await newsletterService.list();
      dispatch({ type: 'FETCH_SUCCESS', payload: Array.isArray(data) ? data : [] });
    } catch (err) {
      dispatch({ type: 'FETCH_FAIL', payload: getError(err) });
    }
  };

  useEffect(() => {
    fetchData();
  }, []);

  const handleDelete = sub => {
    setToDelete(sub);
    setShowDelete(true);
  };

  const closeDelete = () => {
    setShowDelete(false);
    setToDelete(null);
  };

  const confirmDelete = async () => {
    if (!toDelete) return;
    try {
      await newsletterService.remove(toDelete._id);
      toast.success('Suscriptor eliminado');
      closeDelete();
      fetchData();
    } catch (err) {
      toast.error(getError(err));
    }
  };

  const exportCsv = () => {
    if (!subscribers.length) return;
    const header = 'email,fecha\n';
    const body = subscribers.map(s => `${s.email},${s.createdAt || ''}`).join('\n');
    const blob = new Blob([header + body], { type: 'text/csv;charset=utf-8;' });
    const url = window.URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `newsletter-subscribers-${new Date().toISOString().slice(0, 10)}.csv`;
    a.click();
    window.URL.revokeObjectURL(url);
  };

  return (
    <div className='space-y-6'>
      <ConfirmModal
        show={showDelete}
        title='Eliminar suscriptor'
        message={`¿Eliminar ${toDelete?.email}?`}
        onConfirm={confirmDelete}
        onCancel={closeDelete}
        confirmLabel='Eliminar'
        cancelLabel='Cancelar'
        destructive
      />

      <PageHeader
        title='Newsletter'
        subtitle='Emails suscriptos desde el footer del sitio'
        actions={
          <Button variant='outline' onClick={exportCsv} disabled={!subscribers.length}>
            <Download className='h-4 w-4' />
            Exportar CSV
          </Button>
        }
      />

      <Card>
        <CardContent className='p-0'>
          {loading ? (
            <div className='divide-y divide-border'>
              {[0, 1, 2, 3, 4].map(i => (
                <div key={i} className='flex items-center gap-4 px-6 py-4'>
                  <div className='h-3 w-1/3 animate-pulse rounded bg-muted' />
                  <div className='h-3 w-1/4 animate-pulse rounded bg-muted' />
                </div>
              ))}
            </div>
          ) : error ? (
            <p className='px-6 py-5 text-sm text-destructive'>{error}</p>
          ) : subscribers.length === 0 ? (
            <div className='px-6 py-12 text-center text-sm text-muted-foreground'>
              <div className='mx-auto mb-3 flex h-12 w-12 items-center justify-center rounded-full bg-muted'>
                <Mail className='h-5 w-5 text-muted-foreground' />
              </div>
              <p>Todavía no hay suscriptores al newsletter.</p>
            </div>
          ) : (
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead>Email</TableHead>
                  <TableHead>Suscripto desde</TableHead>
                  <TableHead className='w-[80px]' />
                </TableRow>
              </TableHeader>
              <TableBody>
                {subscribers.map(sub => (
                  <TableRow key={sub._id}>
                    <TableCell className='font-medium'>{sub.email}</TableCell>
                    <TableCell className='text-muted-foreground'>
                      {formatDate(sub.createdAt)}
                    </TableCell>
                    <TableCell>
                      <div className='flex items-center justify-end'>
                        <Button
                          variant='ghost'
                          size='icon-sm'
                          title='Eliminar'
                          className='text-destructive hover:text-destructive'
                          onClick={() => handleDelete(sub)}
                        >
                          <Trash2 className='h-4 w-4' />
                        </Button>
                      </div>
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          )}
        </CardContent>
      </Card>
    </div>
  );
}
